<template>
  <!-- A feature page's story chapters (data/feature-stories.ts), in order. Every chapter is a Lazy
       component, so a page downloads only the chapter types its story uses. -->
  <div class="fst">
    <template v-for="(chapter, index) in story.chapters" :key="index">
      <LazyStoryChapters v-if="chapter.type === 'chapters'" :chapter="chapter" />
      <LazyStoryFlow v-else-if="chapter.type === 'flow'" :chapter="chapter" />
      <LazyStoryRewrite v-else-if="chapter.type === 'rewrite'" :chapter="chapter" />
      <LazyStoryGuardrails v-else-if="chapter.type === 'guardrails'" :chapter="chapter" />
      <LazyStoryMeter v-else-if="chapter.type === 'meter'" :chapter="chapter" />
      <LazyStoryPlans v-else-if="chapter.type === 'plan-bars'" :chapter="chapter" />
      <LazyStoryTryIt v-else-if="chapter.type === 'tryit'" :chapter="chapter" />
      <LazyStoryBand v-else-if="chapter.type === 'band'" :chapter="chapter" />
      <LazyStoryTryBooking v-else-if="chapter.type === 'try-booking'" :chapter="chapter" />
      <LazyStoryPlanGrid v-else-if="chapter.type === 'plans'" :chapter="chapter" />
      <LazyStoryPlanLadder v-else-if="chapter.type === 'ladder'" :chapter="chapter" />
      <LazyStoryTryTabs v-else-if="chapter.type === 'tabs'" :chapter="chapter" />
      <LazyStoryHandoff v-else-if="chapter.type === 'handoff'" :chapter="chapter" />
      <LazyStoryTryTimer v-else-if="chapter.type === 'timer'" :chapter="chapter" />
      <LazyStoryLadder v-else-if="chapter.type === 'start-ladder'" :chapter="chapter" />
      <LazyStoryMissedCall v-else-if="chapter.type === 'missedcall'" :chapter="chapter" />
      <LazyStoryKeypad v-else-if="chapter.type === 'keypad'" :chapter="chapter" />
      <LazyStoryPath v-else-if="chapter.type === 'path'" :chapter="chapter" />
      <LazyStoryRing v-else-if="chapter.type === 'ring'" :chapter="chapter" />
      <LazyStoryDecision v-else-if="chapter.type === 'decision'" :chapter="chapter" />
      <LazyStoryApiTry v-else-if="chapter.type === 'api-try'" :chapter="chapter" />
      <LazyStoryWebhooks v-else-if="chapter.type === 'webhooks'" :chapter="chapter" />
      <LazyStoryStats v-else-if="chapter.type === 'stats'" :chapter="chapter" />
      <section v-else-if="chapter.type === 'lanes'" class="fst-lanes">
        <div class="fst-shell fst-lanes-grid">
          <header>
            <h2>{{ chapter.title }}</h2>
            <p class="fst-intro">{{ chapter.intro }}</p>
            <NuxtLink :to="localePath(chapter.path)" class="fst-link">
              {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
            </NuxtLink>
          </header>
          <LazyMotionMcpAppLanes />
        </div>
      </section>
      <slot v-if="index === midAfter" name="mid" />
    </template>
  </div>
</template>

<script setup>
const props = defineProps({
  story: { type: Object, required: true },
});
const { localePath } = useWorkspaceLocale();
// The mid-page call to action goes after the second chapter.
const midAfter = computed(() => Math.min(1, props.story.chapters.length - 1));
</script>

<style scoped>
.fst-lanes { background: #fff; padding: 6.5rem 0; }
.dark .fst-lanes { background: #020617; }
.fst-shell { width: min(100% - 3rem, var(--story-max, 84rem)); margin: 0 auto; }
.fst-lanes-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .fst-lanes-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 4.5rem; } }
.fst-lanes h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .fst-lanes h2 { color: #f8fafc; }
.fst-intro { margin-top: 1rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .fst-intro { color: #cbd5e1; }
.fst-link { display: inline-flex; gap: 0.4rem; margin-top: 1.25rem; color: #0e7490; font-weight: 800; }
.dark .fst-link { color: #67e8f9; }
.fst-link:hover { text-decoration: underline; }
@media (max-width: 639px) {
  .fst-lanes { padding: 4.5rem 0; }
  .fst-shell { width: min(100% - 2rem, var(--story-max, 84rem)); }
}
</style>
